import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronRight, Edit2, Trash2, MessageCircle } from "lucide-react";

const badgeCls = {
  affiliate: "bg-pink-500/20 text-pink-300 border-pink-500/30",
  admin: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  customer: "bg-slate-500/20 text-slate-300 border-slate-500/30",
};

// Normalize a phone number for wa.me — strip everything non-digit; if it looks
// like a 10-digit local number assume India (+91). Otherwise trust the digits.
const waPhone = (raw) => {
  const digits = String(raw || "").replace(/\D/g, "");
  if (digits.length === 10) return `91${digits}`;
  return digits;
};

const loginCodeFor = (name, phone) => {
  const digits = String(phone || "").replace(/\D/g, "");
  const last4 = digits.slice(-4);
  const first4 = String(name || "").replace(/[^a-zA-Z]/g, "").toUpperCase().slice(0, 4);
  return `${first4}${last4}`;
};

const waMessage = (u) => {
  const code = loginCodeFor(u.name, u.phone);
  const url = typeof window !== "undefined" ? window.location.origin : "https://veloskinagilityclub.com";
  const roleLine = u.role === "affiliate"
    ? "As a Veloskin Affiliate you'll earn rewards every time your referrals close — including cash payouts."
    : "Welcome to the Veloskin Agility Club! Earn reward points on every referral and redeem them at our tie-up shops.";
  return (
    `Hi ${u.name?.split(" ")[0] || "there"},\n\n` +
    `${roleLine}\n\n` +
    `Your login details:\n` +
    `• Phone: ${u.phone}\n` +
    `• Login code: ${code}\n\n` +
    `Sign in here: ${url}\n\n` +
    `Tip: Once you're in, tap New Referral to share your code with friends and start earning points.`
  );
};

export default function UserRow({ u, onView, onEdit, onDelete }) {
  const cls = badgeCls[u.role] || badgeCls.customer;
  const joined = u.created_at ? format(new Date(u.created_at), "dd MMM yy") : "-";
  const waLink = `https://wa.me/${waPhone(u.phone)}?text=${encodeURIComponent(waMessage(u))}`;
  const showWa = u.role !== "admin" && waPhone(u.phone).length >= 10;
  return (
    <tr data-testid={`user-row-${u.id}`} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
      <td className="px-6 py-4 text-sm text-white">
        <div>{u.name}</div>
        {u.affiliate_type ? <div className="text-[11px] text-slate-500 mt-0.5">{u.affiliate_type}{u.associate_firm ? ` · ${u.associate_firm}` : ""}</div> : null}
      </td>
      <td className="px-6 py-4 text-sm text-slate-300 font-mono">{u.phone}</td>
      <td className="px-6 py-4"><Badge className={`border ${cls}`}>{u.role}</Badge></td>
      <td className="px-6 py-4 text-sm text-slate-300 font-mono">{u.referral_base || u.referral_code}</td>
      <td className="px-6 py-4">
        {u.role === "admin" ? (
          <span className="text-[11px] text-slate-500">—</span>
        ) : u.setup_complete ? (
          <span data-testid={`setup-badge-${u.id}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 px-2 py-0.5 text-[11px] font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Setup Complete
          </span>
        ) : (
          <span data-testid={`setup-badge-${u.id}`} className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/30 px-2 py-0.5 text-[11px] font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400" /> Pending Setup
          </span>
        )}
      </td>
      <td className="px-6 py-4 text-sm text-white text-right font-mono">{u.points_balance}</td>
      <td className="px-6 py-4 text-xs text-slate-500">{joined}</td>
      <td className="px-6 py-4">
        <div className="flex items-center justify-end gap-1">
          {showWa && (
            <a
              data-testid={`btn-wa-user-${u.id}`}
              href={waLink}
              target="_blank"
              rel="noreferrer"
              title="Send login via WhatsApp"
              className="inline-flex items-center justify-center h-8 w-8 rounded-md text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 transition-colors"
            >
              <MessageCircle className="w-4 h-4" strokeWidth={1.75} />
            </a>
          )}
          <Button data-testid={`btn-view-user-${u.id}`} size="sm" variant="ghost" onClick={onView} className="text-slate-400 hover:text-white hover:bg-white/5 h-8 w-8 p-0" title="View">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button data-testid={`btn-edit-user-${u.id}`} size="sm" variant="ghost" onClick={onEdit} className="text-slate-400 hover:text-pink-300 hover:bg-pink-500/10 h-8 w-8 p-0" title="Edit">
            <Edit2 className="w-3.5 h-3.5" strokeWidth={1.5} />
          </Button>
          <Button data-testid={`btn-delete-user-${u.id}`} size="sm" variant="ghost" onClick={onDelete} className="text-slate-400 hover:text-red-400 hover:bg-red-500/10 h-8 w-8 p-0" title="Delete">
            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
          </Button>
        </div>
      </td>
    </tr>
  );
}
